import type {
  Chat,
  ChatArgType,
  ChatChatId,
  ChatParams,
  ChatQuery,
  SendChatParams,
} from '../useChats/types';
import type { useChatMessages } from '.';

export type UseChatMessagesResult = ReturnType<typeof useChatMessages>;

export enum ChatMessageCategory {
  SEND = 0, // Send
  RECEIVE = 1, // Receive
}

export enum ChatMessageStatus {
  // Message
  LOADING = 0,
  ERROR,
  SUCCESS,
  REACH,
  UNREAD,
  PARTIAL_READ,
  READ,
  // Media
  CALL_HANG_UP = 112, // Call hung up
  CALL_TIMEOUT, // Call timeout (unanswered)
  CALL_CANCELLED, // Call cancelled by initiator
  CALL_REJECTED, // Call rejected
  CALL_BUSY, // Line busy
  CALL_NETWORK_ERROR, // Call dropped due to network error
  CALL_PUT_THROUGH, // Call connected
}

export enum ChatMessagePayloadType {
  TEXT = 0, // Text message
  ENCRYPTED_TEXT = 1, // Encrypted text message
  IMAGE = 2, // Image message
  VIDEO = 3, // Video message
  AUDIO = 4, // Audio message
  FILE = 5, // File message
  ENCRYPTED_EK = 6, // Encrypted EK message

  // Call related
  MEDIA_AUDIO_CALL = 110, // Initiate voice call
  MEDIA_VIDEO_CALL, // Initiate video call

  // Group related
  GROUP_CREATE = 201, // Create group
  GROUP_UPDATE = 202, // Update group info
  GROUP_DISBAND = 203, // Disband group
  GROUP_INVITE = 204, // Invite group members
  GROUP_REMOVE_MEMBER = 205, // Remove group member
  GROUP_MEMBER_QUIT = 206, // Group member quit

  // Group encryption related
  GROUP_ENCRYPTION_WELCOME = 207, // Add member, notify invitee to update welcome
  GROUP_ENCRYPTION_COMMIT = 208, // Member change, notify others to update commit
  GROUP_ENCRYPTION_LEAVE = 209, // Member leave change, notify group owner

  EXPIRE_DURATION = 402, // Message expire time
}

export enum CustomChatMessagePayloadType {
  DIVIDER_CREATE_CHAT = 8887,
  DIVIDER_DATE,
  DIVIDER_TIME,
  ADD_TO_CONTACTS,
}

export enum ChatMessagePulled {
  NO = 0,
  YES = 1,
  ERROR = 2,
}

export enum ChatMessageDeleted {
  NO = 0,
  YES = 1,
}

export enum ChatMessageOwnerId {
  SYSTEM_GROUP_NOTICE = 'SYSTEM_GROUP_NOTICE',
  SYSTEM_PERSONAL_NOTICE = 'SYSTEM_PERSONAL_NOTICE',
}

export const ChatMessageOwnerIdValues = Object.values(ChatMessageOwnerId);

export interface DBChatMessage {
  id: number;
  category: ChatMessageCategory;
  cli_msg_id: string;
  status: ChatMessageStatus;
  message_id: string;
  owner_id: string | ChatMessageOwnerId;
  payload_type: ChatMessagePayloadType | CustomChatMessagePayloadType;
  payload: string;
  link_msg_id: string;
  attach: number;
  edit: number;
  pin: number;
  sticky: number;
  deleted: ChatMessageDeleted;
  pulled: ChatMessagePulled;
  read_at: number;
  create_at: number;
  update_at: number;
  expire_duration: number;
}

export type DBChatMessagePartial = Partial<DBChatMessage>;

export interface ServerChatMessage {
  chatId: string;
  messageId: string;
  msgAction: number;
  expireDuration: number;
  payload: string;
  payloadType: number;
  receiverId: string;
  receiverOrgId: string;
  encryptIndex: number;
  msgSeq: number;
  senderId: string;
  createdAt: number;
  cliMsgId: string;
  linkMsgId?: string;
  expectReadList?: string[];
}

export type ChatMessage = {
  encrypt_index: ServerChatMessage['encryptIndex'];
} & DBChatMessage &
  Partial<ServerChatMessage>;

export type ChatMessagePartial = Partial<ChatMessage>;

export type ChatMessageId = ChatMessage['id'];

export type ChatMessageMessageId = ChatMessage['message_id'];

export type ChatMessageCliMsgId = ChatMessage['cli_msg_id'];

export type ChatMessageKey = ChatMessageId | ChatMessageMessageId | ChatMessageCliMsgId;

export type ChatMessageInfo = {
  index: number;
  message: ChatMessage;
};

export type ChatMessagesInfoMap = Record<ChatMessageKey, ChatMessageInfo>;

export type ChatMessagesItem = {
  messages: ChatMessage[];
  messagesInfoMap: ChatMessagesInfoMap;
};

export type ChatMessagesMap = Record<ChatChatId, ChatMessagesItem>;

export type ChatMessagesLoadParams = ChatParams &
  Pick<Chat, 'category'> & {
    msg_id: number;
    limit: number;
    include_self: boolean;
    current_timestamp: number;
  };

export type ChatMessageIdQuery = Pick<ChatMessage, 'id'>;

export type ChatMessageMessageIdQuery = Pick<ChatMessage, 'message_id'>;

export type ChatMessageCliMsgIdQuery = Pick<ChatMessage, 'cli_msg_id'>;

export type ChatMessageQuery = Partial<
  Pick<ChatMessage, 'id' | 'message_id' | 'cli_msg_id'>
>;

export type ChatMessageIdParams = ChatMessageIdQuery & ChatMessagePartial;

export type ChatMessageMessageIdParams = ChatMessageMessageIdQuery & ChatMessagePartial;

export type ChatMessageParams = ChatMessageQuery & ChatMessagePartial;

export type DBChatMessageParams = ChatMessageQuery & DBChatMessagePartial;

export type SendMessageParams = Pick<ChatMessage, 'payload_type' | 'payload'> &
  ChatMessagePartial & {
    expect_read_list?: string[];
    data?: any;
  };

export type AddMessageParams = ChatQuery & {
  message: SendMessageParams;
  statusMessage?: ChatMessagePartial;
  updateLastRead?: boolean;
};

export type MessageParams<C extends ChatParams, M extends ChatMessageParams> = {
  chat: C;
  message: M;
};

export type SendParams = MessageParams<SendChatParams, SendMessageParams>;
export type RetryParams = MessageParams<Chat, ChatMessage>;

export type ChatMessagesArgType<T> = ChatMessageMessageIdQuery & {
  args: T[];
};

export type DBChatMessagesArg = ChatArgType<DBChatMessage>;

export type DBChatMessagesArgPartial = ChatArgType<DBChatMessagePartial>;

export type DBChatMessagesArgParams = ChatArgType<DBChatMessageParams>;

export type ChatMessagesArg = ChatArgType<ChatMessage>;

export type ChatMessagesArgParams = ChatArgType<ChatMessageParams>;

export type ChatMessagesArgIdParams = ChatArgType<ChatMessageIdParams>;

export type ChatMessagesArgMessageIdParams = ChatArgType<ChatMessageMessageIdParams>;

export type ChatMessagesArgIds = ChatArgType<ChatMessageId>;

export type ChatMessagesArgMessageIds = ChatArgType<ChatMessageMessageId>;
